import { extendZodWithOpenApi, type ZodOpenAPIMetadata } from '@asteasolutions/zod-to-openapi';
import { z } from 'zod';

export { ZodError } from 'zod';

extendZodWithOpenApi(z);

// Re-export to preserve the module augmentation in .d.ts output.
// Without this, TypeScript strips the side-effect import and the
// declare module 'zod' augmentation from @asteasolutions/zod-to-openapi
// is invisible to consuming project references.
export type { ZodOpenAPIMetadata };

export { z };

// ---------------------------------------------------------------------------
// Composable refinements
// ---------------------------------------------------------------------------

/**
 * At most one of the named keys may be present (not `undefined`).
 *
 * @example
 * ```ts
 * z.object({ a: z.string().optional(), b: z.number().optional() })
 *   .superRefine(xor(['a', 'b']));
 * ```
 */
export const xor = <T extends Record<string, unknown>>(keys: string[]) => (data: T, ctx: z.RefinementCtx) => {
  const present = keys.filter((k) => data[k] !== undefined);
  if (present.length > 1) {
    const labels = present.map((k) => `"${k}"`).join(', ');
    ctx.addIssue({
      code: 'custom',
      message: `${labels} are mutually exclusive — at most one may be provided`,
      path: [present[0]],
    });
  }
};

/**
 * All of the named keys must be present together, or all absent together.
 *
 * @example
 * ```ts
 * z.object({ type: z.string().optional(), id: z.string().optional() })
 *   .superRefine(and(['type', 'id']));
 * ```
 */
export const and = <T extends Record<string, unknown>>(keys: string[]) => (data: T, ctx: z.RefinementCtx) => {
  const present = keys.filter((k) => data[k] !== undefined);
  if (present.length > 0 && present.length < keys.length) {
    const labels = keys.map((k) => `"${k}"`).join(' and ');
    ctx.addIssue({
      code: 'custom',
      message: `${labels} must all be provided or all omitted`,
      path: [present[0]],
    });
  }
};

// ---------------------------------------------------------------------------
// Record / object shape refinements
// ---------------------------------------------------------------------------

/**
 * Validates that a record/object has at most `max` keys.
 *
 * @example
 * ```ts
 * z.record(z.string(), z.string()).superRefine(maxKeys(50));
 * ```
 */
export const maxKeys = (max: number) => (data: Record<string, unknown>, ctx: z.RefinementCtx) => {
  if (Object.keys(data).length > max) {
    ctx.addIssue({
      code: 'custom',
      message: `Must have no more than ${max} keys`,
    });
  }
};

/**
 * At least one value of the object must be non-`undefined`.
 * When `keys` are provided, issues are added per-path with explicit names.
 *
 * @example
 * ```ts
 * z.object({ a: z.string().optional(), b: z.number().optional() })
 *   .superRefine(atLeastOne);
 * ```
 *
 * @example
 * ```ts
 * z.object({ a: z.string().optional(), b: z.number().optional() })
 *   .superRefine(atLeastOne(['a', 'b']));
 * ```
 */
export const atLeastOne = (keys?: string[]) => (data: Record<string, unknown>, ctx: z.RefinementCtx) => {
  if (keys && keys.length > 0) {
    if (keys.every((k) => data[k] === undefined)) {
      const labels = keys.join(' or ');
      for (const key of keys) {
        ctx.addIssue({
          code: 'custom',
          message: `at least one of ${labels} is required`,
          path: [key],
        });
      }
    }
  } else if (Object.values(data).every((v) => v === undefined)) {
    ctx.addIssue({
      code: 'custom',
      message: 'Must contain at least 1 key',
    });
  }
};

// ---------------------------------------------------------------------------
// Type-level helpers
// ---------------------------------------------------------------------------

/**
 * Accepts a single value or an array — always normalizes to an array.
 * Replaces Joi's `.single()` behaviour for query parameters.
 *
 * @example
 * ```ts
 * singleOrArray(z.string())  // accepts "foo" or ["foo", "bar"]
 * ```
 */
export const singleOrArray = <T extends z.ZodTypeAny>(
  schema: T,
) => z.union([schema.transform((v) => [v as z.output<T>]), z.array(schema)]);
